/**
 * Centralized date/number formatting utilities.
 *
 * All components should import these instead of defining local copies.
 * Date-string conventions in this app:
 * - Bare dates (expirations, NAV dates) are "YYYY-MM-DD" and must be parsed as
 *   LOCAL time — new Date('2026-01-30') parses as UTC and renders a day early
 *   in US timezones.
 * - Backend timestamps (entry/exit dates, created_at, last_login) may be naive
 *   UTC or tz-aware EST ("...-04:00"). parseUtcTimestamp handles both; parseInstant also takes
 *   the RFC-1123 "... GMT" form Flask gives a raw Firestore datetime. The DAY of an instant is
 *   always the ET day (B-026), in the date-only formatters too.
 */

// HARD RULE (CLAUDE.md): every timestamp shown anywhere is rendered in ET, regardless of the
// viewer's location. All time-bearing formatters force timeZone America/New_York and label "ET".
const ET = 'America/New_York';

const parseUtcTimestamp = (dateStr: string): Date => {
  // Backend timestamps may be:
  //   naive UTC: "2026-06-19T09:50:00.123456" → append Z
  //   tz-aware EST: "2026-06-19T09:50:00.123456-04:00" → parse as-is
  if (dateStr.endsWith('Z') || /[+-]\d{2}:\d{2}$/.test(dateStr)) {
    return new Date(dateStr);
  }
  return new Date(dateStr + 'Z');
};

/** A stored instant: ISO without an offset is legacy naive UTC, with a "T" or with Python's
 *  str(datetime) space; anything else (ISO with an offset, or the "Thu, 01 Oct 2026 03:30:00 GMT"
 *  form Flask gives a Firestore datetime) is parsed as written. */
export const parseInstant = (dateStr: string): Date =>
  /^\d{4}-\d{2}-\d{2}[T ]\d/.test(dateStr)
    ? parseUtcTimestamp(dateStr.replace(' ', 'T'))
    : new Date(dateStr);

/** The calendar day of a date string (B-026). A bare YYYY-MM-DD already IS the day and is shown
 *  as written; anything else is an instant, and its day is the ET day — never the viewer's. */
const formatDay = (dateStr: string, opts: Intl.DateTimeFormatOptions): string => {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(dateStr);
  if (m) return new Date(+m[1], +m[2] - 1, +m[3]).toLocaleDateString('en-US', opts);
  const d = parseInstant(dateStr);
  if (isNaN(d.getTime())) return dateStr;
  return d.toLocaleDateString('en-US', { ...opts, timeZone: ET });
};

/** "Jan 30" — expirations, NAV dates, close dates. */
export const formatDateShort = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  try {
    return formatDay(dateStr, { month: 'short', day: 'numeric' });
  } catch {
    return dateStr;
  }
};

/** "Jan 30, 2026" */
export const formatDateWithYear = (dateStr?: string): string => {
  if (!dateStr) return 'N/A';
  try {
    return formatDay(dateStr, { month: 'short', day: 'numeric', year: 'numeric' });
  } catch {
    return dateStr;
  }
};

/** "Jan 30 2:05 PM ET" — backend timestamps (naive UTC or tz-aware EST), always shown in ET. */
export const formatDateTime = (dateStr: string): string => {
  try {
    const d = parseUtcTimestamp(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return (
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', timeZone: ET }) +
      ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: ET }) +
      ' ET'
    );
  } catch {
    return dateStr;
  }
};

/** "Jan 30, 2026 2:05 PM ET" — backend timestamps, always shown in ET. */
export const formatDateTimeWithYear = (dateStr: string): string => {
  try {
    const d = parseUtcTimestamp(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return (
      d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', timeZone: ET }) +
      ' ' +
      d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit', timeZone: ET }) +
      ' ET'
    );
  } catch {
    return dateStr;
  }
};

/** Canonical full timestamp formatter (CLAUDE.md: frontend → formatET). "Jan 30, 2026 2:05 PM ET". */
export const formatET = formatDateTimeWithYear;

/** "2:05 PM ET" — backend timestamps, always shown in ET. */
export const formatTime = (dateStr: string): string => {
  try {
    const d = parseUtcTimestamp(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    return d.toLocaleTimeString('en-US', {
      hour: 'numeric',
      minute: '2-digit',
      timeZone: ET,
    }) + ' ET';
  } catch {
    return dateStr;
  }
};

/** "Today" / "Yesterday" / "Monday, June 1" — backend timestamps, compared in ET. */
export const formatDateHeader = (dateStr: string): string => {
  try {
    const d = parseUtcTimestamp(dateStr);
    if (isNaN(d.getTime())) return dateStr;
    // Compare calendar dates in ET (not browser-local), so "Today" means today in Eastern.
    const etDate = (x: Date) => x.toLocaleDateString('en-US', { timeZone: ET });
    const now = new Date();
    const yest = new Date(now.getTime() - 86400000);
    if (etDate(d) === etDate(now)) return 'Today';
    if (etDate(d) === etDate(yest)) return 'Yesterday';
    return d.toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', timeZone: ET });
  } catch {
    return dateStr;
  }
};

/** "+1.23%" / "-0.45%" */
export const formatSignedPct = (value: number, decimals: number = 2): string =>
  `${value >= 0 ? '+' : ''}${value.toFixed(decimals)}%`;

/** "$1,234.56" */
export const formatCurrency = (value: number): string =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 2,
  }).format(value);
